import { useEffect, useState } from "react";
import {
  ApiError,
  breakGlassExport,
  downloadExport,
  readAuditRecord,
  verifyIntegrity,
  type AdminAuditRecord,
  type ExportResult,
  type IntegrityCheck,
} from "../api/client";
import { Callout } from "../components/Callout";
import { TrailButton, TrailDrawer } from "../components/TrailDrawer";
import { TextAreaField } from "../components/TextAreaField";
import { TextField } from "../components/TextField";
import { idempotencyKey } from "../api/idempotency";

/**
 * Screen 72 — the audit record an administrator reaches, and the record that reach leaves.
 *
 * The prototype's caption is the whole design: *"This read has been logged. An access event
 * naming you was written to the audit trail and cannot be removed."* Everything on
 * this screen is arranged around that sentence being true. The caption itself is no longer
 * drawn — that was a presentation decision and it changed nothing below it.
 *
 * **The event is already on the trail this screen renders.** `audit.record_read` is appended
 * before the record is assembled, so the first entry of the trail panel is the read that
 * produced the page. That is not a presentation choice — it is the order the route runs in,
 * and it is what makes the logging a fact rather than a promise. Writing the
 * event afterwards would make the record of a reach depend on the reach having succeeded,
 * and the interesting reaches are the ones somebody would rather did not complete.
 *
 * **Nothing here is editable, and there is no route that would let it be.** M6-2 is a read;
 * the two things an administrator can *do* from this screen are M5-7's, below the record.
 *
 * **Two answers, and the first is almost always the right one.** Most support cases are
 * integrity questions rather than content questions — *is the record intact*, not *what does
 * it say*. `verify_integrity` recomputes and compares the chain without producing a readable
 * document, and it is offered first because keeping content export rare is what makes the
 * break-glass framing honest rather than decorative.
 *
 * **The justification is written before the export runs**, and it is what the certification
 * body reads on its own trail. Not a note added afterwards about something that already
 * happened.
 *
 * **Suppressing the notification takes a named approver.** Cl. 8.4 requires the client to be
 * told where confidential information is released, so a break-glass row either carries the
 * moment the body was told or the person who approved not telling them. The policy behind
 * suppression is unresolved and sits with the platform's legal review; the shape is not.
 *
 * One artefact, one audit, one call. There is no bulk form of this screen.
 */
export function AuditRecord({
  auditId,
  tenantId,
  tenantName,
  onBack,
}: {
  auditId: string;
  tenantId: string;
  /** The list row's label, absent when the record was reached by URL. See `adminPath`. */
  tenantName?: string;
  onBack: () => void;
}) {
  const [record, setRecord] = useState<AdminAuditRecord | null>(null);
  const [check, setCheck] = useState<IntegrityCheck | null>(null);
  const [result, setResult] = useState<ExportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reaching, setReaching] = useState(false);
  /** The trail, read beside the record rather than below it — see `TrailDrawer`. */
  const [trailOpen, setTrailOpen] = useState(false);

  useEffect(() => {
    readAuditRecord(auditId, tenantId)
      .then(setRecord)
      .catch((cause: unknown) => setError(problem(cause, "The record")));
  }, [auditId, tenantId]);

  const verify = async () => {
    setBusy(true);
    setError(null);
    try {
      setCheck(await verifyIntegrity(auditId, tenantId));
    } catch (cause) {
      setError(problem(cause, "The integrity check"));
    } finally {
      setBusy(false);
    }
  };

  const reach = async (request: {
    kind: "plan" | "report";
    justification: string;
    suppressNotification: boolean;
    suppressionApproverId: string;
  }) => {
    setBusy(true);
    setError(null);
    try {
      const exported = await breakGlassExport(
        auditId,
        tenantId,
        {
          kind: request.kind,
          justification: request.justification,
          suppressNotification: request.suppressNotification,
          suppressionApproverId: request.suppressionApproverId || undefined,
        },
        idempotencyKey(),
      );
      const blob = await downloadExport(auditId, exported.export_id);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `break-glass-${request.kind}.pdf`;
      anchor.click();
      URL.revokeObjectURL(url);
      setResult(exported);
      setReaching(false);
    } catch (cause) {
      setError(problem(cause, "The export"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" className="backlink linkish" onClick={onBack}>
        ← All audits
      </button>

      <div className="between" style={{ marginBottom: "14px" }}>
        <div>
          <div className="eyebrow">
            Read-only &middot; {record?.certification_body ?? tenantName ?? ""}
          </div>
          <h1>{record?.client_organisation ?? "Audit record"}</h1>
          <p className="sub">
            <span className="mono">{record?.reference ?? auditId}</span>
            {record?.standards?.length ? ` · ${record.standards.join(" · ")}` : null}
          </p>
        </div>
        {record ? (
          <div style={{ textAlign: "right" }}>
            <span className="pill p-mute">{stateLabel(record.state)}</span>
            <div style={{ marginTop: "6px" }}>
              <Approval label="CB" at={record.cb_approved_at} />{" "}
              <Approval label="LA" at={record.lead_auditor_approved_at} />
            </div>
            <div style={{ marginTop: "8px" }}>
              <TrailButton count={record.trail.length} onOpen={() => setTrailOpen(true)} />
            </div>
          </div>
        ) : null}
      </div>

      {error ? (
        <p className="problem" role="alert">
          {error}
        </p>
      ) : null}

      {record ? (
        <>
          <div className="grid2">
            <div className="card">
              <h2>Audit record</h2>
              <dl className="kv">
                <dt>Client</dt>
                <dd>{record.client_organisation}</dd>
                <dt>Scope</dt>
                <dd>{record.certification_scope}</dd>
                <dt>Products and services</dt>
                <dd>{record.products_services}</dd>
                <dt>Lead auditor</dt>
                <dd>{record.lead_auditor ?? "—"}</dd>
                <dt>Planned</dt>
                <dd>{record.planned_date ?? "—"}</dd>
                <dt>Consultancy declared</dt>
                <dd>
                  {record.consultancy_declared ? (
                    <span className="pill p-red">Yes — Cl. 4.2 review</span>
                  ) : (
                    <span className="pill p-ok">No</span>
                  )}
                </dd>
                <dt>Effective personnel</dt>
                <dd>{record.effective_personnel ?? "—"}</dd>
              </dl>
            </div>

            <div className="card">
              <h2>Duration</h2>
              {record.duration ? (
                <>
                  {/*
                    The administrator's ledger reads in the unit the certification body set,
                    which is Stage 1 hours — the same derivation screen 30 shows, projected
                    through `read_agreement`. The calculated audit time in days stays as the
                    line above it, because it is the MD 5 figure the agreement is measured
                    against and it is not what anybody here set.
                  */}
                  <div className="ledger">
                    <div className="base">
                      <span>
                        Calculated Stage 1 allocation
                        <small>
                          of {record.duration.adjusted_days} d calculated audit time
                        </small>
                      </span>
                      <span className="v">
                        {record.duration.calculated_stage1_hours} h
                      </span>
                    </div>
                    {record.duration.adjustments.map((entry) => (
                      <div
                        key={entry.id}
                        className={Number(entry.hours) < 0 ? "neg" : undefined}
                      >
                        <span>
                          {label(entry.type)}
                          <small>{entry.reason}</small>
                        </span>
                        <span className="v">{entry.hours} h</span>
                      </div>
                    ))}
                    <div className="tot">
                      <span>Agreed</span>
                      <span className="v">
                        {record.duration.agreed_stage1_hours} h
                      </span>
                    </div>
                  </div>
                  <div className="sub" style={{ marginTop: "8px" }}>
                    {record.duration.percent_of_calculated}% of the calculated Stage 1
                    allocation &middot; floor {record.duration.floor_hours} h &middot; total
                    agreed {record.duration.agreed_days} d
                  </div>
                  {record.duration.below_floor ? (
                    <div className="warnbar" style={{ marginTop: "10px" }}>
                      {record.duration.message} {record.duration.clause}
                    </div>
                  ) : null}
                </>
              ) : (
                <p className="sub">
                  No determination has been made for this audit. A draft intake has none, and
                  a re-confirmation invalidates the one that was.
                </p>
              )}
            </div>
          </div>

          {/*
            The trail is the drawer at the masthead. The callout above still says "the audit
            trail" and means it: the access event naming this reader is the first line of
            the panel, one click from where they are being told about it.
          */}
          <TrailDrawer
            open={trailOpen}
            onClose={() => setTrailOpen(false)}
            entries={record.trail}
            describe={(entry) => sentence(entry.event_type)}
            marked={(entry) => CROSS_TENANT.has(entry.event_type)}
          />
        </>
      ) : error ? null : (
        <div className="card">Opening the record&hellip;</div>
      )}

      <div className="card">
        <h2>Verify record integrity</h2>
        <p style={{ fontSize: "13px" }}>
          Recomputes the hash chain and compares it, event by event, and produces{" "}
          <b>no readable document</b>. Most support cases are integrity questions rather
          than content questions, and this answers them without releasing a client&rsquo;s
          audit content.
        </p>
        <div className="row" style={{ justifyContent: "flex-end", marginTop: "10px" }}>
          <button type="button" className="btn sec" disabled={busy} onClick={() => void verify()}>
            {busy ? "Verifying…" : "Verify integrity"}
          </button>
        </div>

        {check ? (
          <Callout
            tone={check.chain_intact ? "info" : "warn"}
            title={
              check.chain_intact
                ? `Chain intact — ${check.events} events verified.`
                : "The chain does not verify."
            }
          >
            <div className="mono" style={{ fontSize: "11px", wordBreak: "break-all" }}>
              Record {check.record_hash}
            </div>
            {check.broken_at ? <div style={{ marginTop: "6px" }}>{check.broken_at}</div> : null}
          </Callout>
        ) : null}
      </div>

      <div className="card">
        <h2>Break-glass export</h2>
        <p style={{ fontSize: "13px" }}>
          Lead-auditor-signed and issued artefacts only &mdash; never a draft, never an
          in-flight session. One artefact, one audit, one call. The justification is recorded
          on the certification body&rsquo;s own trail <b>before</b> the export runs, and the
          body&rsquo;s contact is notified unless a named approver has authorised
          suppression.
        </p>
        <p className="sub" style={{ fontSize: "11px" }}>
          Genuine cases: a disputed render, an accreditation body investigation, a legal
          hold, a body being offboarded whose records must be reconstructed. &ldquo;Ask the
          certification body to export it&rdquo; fails exactly when the body is the party in
          question.
        </p>
        <div className="row" style={{ justifyContent: "flex-end", marginTop: "10px" }}>
          <button type="button" className="btn sec" disabled={busy} onClick={() => setReaching(true)}>
            Break-glass export…
          </button>
        </div>

        {result ? (
          <Callout tone="warn" title="Export recorded on the certification body's trail.">
            Grant <b>{result.grant}</b> &middot; state at export{" "}
            <b>{result.state_at_export ?? "—"}</b>.
            <div className="mono" style={{ fontSize: "11px", wordBreak: "break-all" }}>
              Record {result.record_hash}
            </div>
            {result.notified_tenant_at
              ? `The certification body's contact was notified at ${stamp(result.notified_tenant_at)}.`
              : "Notification suppressed, with the named approver recorded on the event."}
          </Callout>
        ) : null}
      </div>

      {reaching ? (
        <BreakGlassDialog
          busy={busy}
          onCancel={() => setReaching(false)}
          onConfirm={(request) => void reach(request)}
        />
      ) : null}
    </>
  );
}

/**
 * The Step 2 approvals, side by side and never merged.
 *
 * An absent approval renders as *pending* rather than blank. Blank reads as *not
 * applicable*, and an audit cannot proceed on one of the two.
 */
function Approval({ label: party, at }: { label: string; at: string | null | undefined }) {
  return at ? (
    <span className="pill p-ok">{party} approved</span>
  ) : (
    <span className="pill p-wait">{party} pending</span>
  );
}

/** The events that record somebody reaching in from outside the certification body. */
const CROSS_TENANT = new Set([
  "audit.record_read",
  "audit.session_read",
  "audit.break_glass_export",
  "audit.integrity_verified",
]);

/**
 * The event, in words.
 *
 * Only the events this screen exists to make visible are spelled out; everything else falls
 * back to its own type rather than to an invented sentence. A trail line that says something
 * the payload does not is worse than one that says less.
 */
function sentence(eventType: string): string {
  switch (eventType) {
    case "audit.record_read":
      return "Cross-tenant read by Complao administrator.";
    case "audit.session_read":
      return "Live session observed by Complao administrator.";
    case "audit.break_glass_export":
      return "Break-glass export by Complao administrator. Justification recorded before the export ran.";
    case "audit.integrity_verified":
      return "Record integrity verified by Complao administrator. No document produced.";
    default:
      return eventType;
  }
}

function label(value: string): string {
  const words = value.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function stateLabel(state: string): string {
  return label(state);
}

function BreakGlassDialog({
  busy,
  onCancel,
  onConfirm,
}: {
  busy: boolean;
  onCancel: () => void;
  onConfirm: (request: {
    kind: "plan" | "report";
    justification: string;
    suppressNotification: boolean;
    suppressionApproverId: string;
  }) => void;
}) {
  const [kind, setKind] = useState<"plan" | "report">("report");
  const [justification, setJustification] = useState("");
  const [suppress, setSuppress] = useState(false);
  const [approver, setApprover] = useState("");
  const tooShort = justification.trim().length < 10;
  const needsApprover = suppress && !approver.trim();

  return (
    <div
      className="scrim"
      onClick={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true">
        <h2>Break-glass export</h2>
        <p className="sub" style={{ marginBottom: "14px" }}>
          This releases a certification body&rsquo;s client artefact to Complao. The
          justification below is written to that body&rsquo;s own audit trail before the
          document is rendered, and it is what they read when they see that the platform
          reached into their client&rsquo;s file.
        </p>

        <div className="field">
          <label className="f">Artefact</label>
          <div className="row" style={{ gap: "10px" }}>
            {(["report", "plan"] as const).map((value) => (
              <button
                key={value}
                type="button"
                className={kind === value ? "btn sm" : "btn sec sm"}
                onClick={() => setKind(value)}
              >
                {value === "report" ? "Audit report" : "Audit plan"}
              </button>
            ))}
          </div>
        </div>

        <TextAreaField
          id="break-glass-justification"
          label="Justification"
          value={justification}
          onChange={setJustification}
          rows={3}
          required
          error={justification && tooShort ? "At least 10 characters." : undefined}
          hint="Recorded before the export runs, on the certification body's own trail."
        />

        <label className="check" style={{ display: "block", fontSize: "13px" }}>
          <input
            type="checkbox"
            checked={suppress}
            onChange={(event) => setSuppress(event.target.checked)}
          />{" "}
          Suppress notification to the certification body
        </label>
        {suppress ? (
          <>
            <Callout tone="warn" title="Legal hold or law-enforcement request only.">
              Notifying the certification body would defeat the purpose in those cases and
              in no others. This takes a named approver recorded on the event, not a
              checkbox. The policy behind it is unresolved and sits with legal review.
            </Callout>
            <TextField
              id="break-glass-approver"
              label="Approver (user id)"
              value={approver}
              onChange={setApprover}
              required
            />
          </>
        ) : null}

        <div className="mfoot">
          <button type="button" className="btn sec" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || tooShort || needsApprover}
            onClick={() =>
              onConfirm({
                kind,
                justification,
                suppressNotification: suppress,
                suppressionApproverId: approver.trim(),
              })
            }
          >
            {busy ? "Exporting…" : "Record justification and export"}
          </button>
        </div>
      </div>
    </div>
  );
}

function stamp(value: string | null | undefined): string {
  return value ? value.slice(0, 16).replace("T", " ") : "—";
}

function problem(cause: unknown, what: string): string {
  return cause instanceof ApiError ? cause.message : `${what} failed.`;
}
